import React, { useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import AdminDashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import AccountDashboard from "./pages/AccountDashboard";
import OfficerDashboard from "./pages/OfficerDashboard";
import UserDashboard from "./pages/UserDashboard";
import RequestsPortal from "./pages/RequestsPortal"; // ✅ Added Grievance page
import Login from "./pages/Login";
import Navbar from "./components/Navbar";
import LoadingScreen from "./components/LoadingScreen";
import { AuthProvider, AuthContext } from "./context/AuthContext";

// PrivateRoute with role check
function PrivateRoute({ children, roles }) {
  const { user, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If roles are passed, check if user role matches
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" />; // redirect unauthorized users
  }

  return children;
}

// Helper: Decide which dashboard to load based on role
function RoleBasedDashboard() {
  const { user, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <LoadingScreen />;
  }

  switch (user.role) {
    case "admin":
      return <AdminDashboard />;
    case "accounts":
      return <AccountDashboard />;
    case "officer":
      return <OfficerDashboard />;
    case "user":
      return <UserDashboard />;
    default:
      return <Navigate to="/login" />;
  }
}

function AppContent() {
  const { isLoading, user } = useContext(AuthContext);

  if (isLoading) {
    return <LoadingScreen userRole={user?.role} />;
  }

  return (
    <Router>
      {user && <Navbar />}
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={<Login />} />

        {/* Default redirect after login */}
        <Route
          path="/"
          element={
            <PrivateRoute roles={["admin", "accounts", "officer", "user"]}>
              <RoleBasedDashboard />
            </PrivateRoute>
          }
        />

        {/* Admin-specific pages */}
        <Route
          path="/admin"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminPanel />
            </PrivateRoute>
          }
        />

        {/* Accounts Dashboard */}
        <Route
          path="/accounts"
          element={
            <PrivateRoute roles={["accounts"]}>
              <AccountDashboard />
            </PrivateRoute>
          }
        />

        {/* Officer Dashboard */}
        <Route
          path="/officer"
          element={
            <PrivateRoute roles={["officer"]}>
              <OfficerDashboard />
            </PrivateRoute>
          }
        />

        {/* User Dashboard */}
        <Route
          path="/resident"
          element={
            <PrivateRoute roles={["resident"]}>
              <UserDashboard />
            </PrivateRoute>
          }
        />

        {/* ✅ Grievance Page */}
        <Route
          path="/requests"
          element={
            <PrivateRoute roles={["resident", "officer", "accounts", "admin"]}>
              <RequestsPortal />
            </PrivateRoute>
          }
        />

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
